import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";
import {
  Menu,
  X,
  Store,
  ShoppingCart,
  Users,
  Building2,
  LogOut,
} from "lucide-react";

// Auth Context & Protected Route imports
import { AuthProvider, useAuth } from "./services/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Page Imports
import LoginPage from "./pages/LoginPage";
import CommissionReport from "./pages/CommissionReport";
import CommissionTiersPage from "./pages/CommissionTiersPage";
import DistrictManagers from "./pages/DistrictManagerPage";
import EcomCommissions from "./pages/EcomCommissions";
import DistrictManagerCommissions from "./pages/DistrictManagerCommissions";
import BuildersCommission from "./pages/BuildersCommission";
import StoreCommissions from "./pages/StoreCommissions";
import BuilderCrudManager from "./pages/BuilderCommissionManger";
import EcommCommissionManager from "./pages/EcommCommissionManager";

const Navigation = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    {
      path: "/",
      label: "Store",
      fullLabel: "Store Commissions",
      icon: Store,
      color: "blue",
    },
    {
      path: "/ecom-commissions",
      label: "E-Commerce",
      fullLabel: "E-Commerce",
      icon: ShoppingCart,
      color: "emerald",
    },
    {
      path: "/district-managers",
      label: "District",
      fullLabel: "District Managers",
      icon: Users,
      color: "purple",
    },
    {
      path: "/builders",
      label: "Builders",
      fullLabel: "Builders",
      icon: Building2,
      color: "orange",
    },
  ];

  // Don't render the top navigation bar if the user is on the login page or not logged in
  if (!user || location.pathname === "/login") {
    return null;
  }

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Brand */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-slate-900 group-hover:text-blue-700 transition-colors">
                EE Commission
              </h1>
              <p className="text-xs text-slate-500 -mt-0.5">
                Management System
              </p>
            </div>
          </Link>

          {/* Desktop Navigation - Tab Style */}
          <div className="hidden md:flex items-center gap-1 bg-slate-50 rounded-xl p-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const active = isActive(link.path);

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={` 
                    flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200
                    ${
                      active
                        ? "bg-white text-blue-700 shadow-sm"
                        : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden lg:inline">{link.fullLabel}</span>
                  <span className="lg:hidden">{link.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Right Side - User Info & Logout Button */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-200">
              <span className="text-xs font-semibold text-slate-700">
                {user.email}
              </span>
            </div>

            <button
              onClick={logout}
              title="Logout"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-slate-600 hover:text-red-600 hover:bg-red-50 font-medium text-xs border border-slate-200 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 animate-slideDown">
            <div className="space-y-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const active = isActive(link.path);

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={` 
                      flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200
                      ${
                        active
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    {link.fullLabel}
                  </Link>
                );
              })}

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  logout();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm text-red-600 hover:bg-red-50 transition-all duration-200 mt-2 border-t border-slate-100"
              >
                <LogOut className="w-5 h-5" />
                Logout ({user.email})
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
      `}</style>
    </nav>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
          <Navigation />
          <main>
            <Routes>
              {/* Public Route */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected Routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<StoreCommissions />} />
                <Route
                  path="/store-comm-tiers"
                  element={<CommissionTiersPage />}
                />
                <Route path="/report" element={<CommissionReport />} />
                <Route path="/ecom-commissions" element={<EcomCommissions />} />
                <Route
                  path="/ecom-commissions/settings"
                  element={<EcommCommissionManager />}
                />
                <Route
                  path="/district-managers"
                  element={<DistrictManagerCommissions />}
                />
                <Route path="/dm-comm-tiers" element={<DistrictManagers />} />
                <Route path="/builders" element={<BuildersCommission />} />
                <Route
                  path="/builders/setting"
                  element={<BuilderCrudManager />}
                />
              </Route>

              {/* Fallback Route */}
              <Route path="*" element={<CommissionReport />} />
            </Routes>
          </main>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;

// import React, { useState } from "react";
// import {
//   BrowserRouter as Router,
//   Routes,
//   Route,
//   Link,
//   useLocation,
// } from "react-router-dom";
// import CommissionReport from "./pages/CommissionReport";
// import CommissionTiersPage from "./pages/CommissionTiersPage";
// import DistrictManagers from "./pages/DistrictManagerPage";
// import HomePage from "./pages/HomePage";
// import EcomCommissions from "./pages/EcomCommissions";
// import DistrictManagerCommissions from "./pages/DistrictManagerCommissions";
// import BuildersCommission from "./pages/BuildersCommission";
// import { Menu, X, Store, ShoppingCart, Users, Building2 } from "lucide-react";
// import StoreCommissions from "./pages/StoreCommissions";
// import BuilderCrudManager from "./pages/BuilderCommissionManger";
// import EcommCommissionManager from "./pages/EcommCommissionManager";

// const Navigation = () => {
//   const location = useLocation();
//   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

//   const isActive = (path) => location.pathname === path;

//   const navLinks = [
//     {
//       path: "/",
//       label: "Store",
//       fullLabel: "Store Commissions",
//       icon: Store,
//       color: "blue",
//     },
//     {
//       path: "/ecom-commissions",
//       label: "E-Commerce",
//       fullLabel: "E-Commerce",
//       icon: ShoppingCart,
//       color: "emerald",
//     },
//     {
//       path: "/district-managers",
//       label: "District",
//       fullLabel: "District Managers",
//       icon: Users,
//       color: "purple",
//     },
//     {
//       path: "/builders",
//       label: "Builders",
//       fullLabel: "Builders",
//       icon: Building2,
//       color: "orange",
//     },
//   ];

//   return (
//     <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
//       <div className="mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="flex justify-between items-center h-16">
//           {/* Logo/Brand */}
//           <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
//             <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200">
//               <svg
//                 className="w-6 h-6 text-white"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2.5}
//                   d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
//                 />
//               </svg>
//             </div>
//             <div className="hidden sm:block">
//               <h1 className="text-xl font-bold text-slate-900 group-hover:text-blue-700 transition-colors">
//                 EE Commission
//               </h1>
//               <p className="text-xs text-slate-500 -mt-0.5">
//                 Management System
//               </p>
//             </div>
//           </Link>

//           {/* Desktop Navigation - Tab Style */}
//           <div className="hidden md:flex items-center gap-1 bg-slate-50 rounded-xl p-1">
//             {navLinks.map((link) => {
//               const Icon = link.icon;
//               const active = isActive(link.path);

//               return (
//                 <Link
//                   key={link.path}
//                   to={link.path}
//                   className={`
//                     flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200
//                     ${
//                       active
//                         ? "bg-white text-blue-700 shadow-sm"
//                         : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
//                     }
//                   `}
//                 >
//                   <Icon className="w-4 h-4" />
//                   <span className="hidden lg:inline">{link.fullLabel}</span>
//                   <span className="lg:hidden">{link.label}</span>
//                 </Link>
//               );
//             })}
//           </div>

//           {/* Right Side - Status & Mobile Menu */}
//           <div className="flex items-center gap-3">
//             {/* Live Status Badge */}
//             <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg">
//               {/* <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
//               <span className="text-sm font-medium text-slate-700">Live</span> */}
//             </div>

//             {/* Mobile Menu Button */}
//             <button
//               onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
//               className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
//             >
//               {mobileMenuOpen ? (
//                 <X className="w-6 h-6" />
//               ) : (
//                 <Menu className="w-6 h-6" />
//               )}
//             </button>
//           </div>
//         </div>

//         {/* Mobile Menu */}
//         {mobileMenuOpen && (
//           <div className="md:hidden py-4 border-t border-slate-200 animate-slideDown">
//             <div className="space-y-1">
//               {navLinks.map((link) => {
//                 const Icon = link.icon;
//                 const active = isActive(link.path);

//                 return (
//                   <Link
//                     key={link.path}
//                     to={link.path}
//                     onClick={() => setMobileMenuOpen(false)}
//                     className={`
//                       flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200
//                       ${
//                         active
//                           ? "bg-blue-50 text-blue-700"
//                           : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
//                       }
//                     `}
//                   >
//                     <Icon className="w-5 h-5" />
//                     {link.fullLabel}
//                   </Link>
//                 );
//               })}
//             </div>
//           </div>
//         )}
//       </div>

//       <style jsx>{`
//         @keyframes slideDown {
//           from {
//             opacity: 0;
//             transform: translateY(-10px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }

//         .animate-slideDown {
//           animation: slideDown 0.2s ease-out;
//         }
//       `}</style>
//     </nav>
//   );
// };

// const App = () => {
//   return (
//     <Router>
//       <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
//         <Navigation />
//         <main>
//           <Routes>
//             <Route path="/store-comm-tiers" element={<CommissionTiersPage />} />
//             <Route path="/report" element={<CommissionReport />} />
//             <Route path="/" element={<StoreCommissions />} />
//             <Route path="/ecom-commissions" element={<EcomCommissions />} />
//             <Route
//               path="/ecom-commissions/settings"
//               element={<EcommCommissionManager />}
//             />

//             <Route
//               path="/district-managers"
//               element={<DistrictManagerCommissions />}
//             />

//             <Route path="/dm-comm-tiers" element={<DistrictManagers />} />

//             <Route path="/builders" element={<BuildersCommission />} />
//             <Route path="/builders/setting" element={<BuilderCrudManager />} />
//             <Route path="*" element={<CommissionReport />} />
//           </Routes>
//         </main>
//       </div>
//     </Router>
//   );
// };

// export default App;
